export interface Category {
  id: string;
  code: string;
  name: string;
  description?: string; // Changed from notes to match backend
  type: CategoryType; // Added type field
  parentId?: string; // Added parent ID for hierarchy
  parentName?: string; // Added parent name for display
  for_semi_material?: boolean; // Added semi-material flag
  status: CategoryStatus;
  createdAt: string;
  updatedAt: string;
  // Counts and statistics
  materialCount?: number;
  productCount?: number; // Added product count from backend
  linkedRawMaterials: number; // Count of linked raw materials (computed from materialCount)
  // Additional fields for business logic
  creator: string;
  createdDate: string; // Computed from createdAt for display
  lastUsedDate?: string;
  // Hierarchy support
  children?: Category[];
}

export type CategoryStatus = 'active' | 'inactive';

// Category types based on backend API
export type CategoryType =
  | 'material_category'
  | 'material_group'
  | 'customer_group'
  | 'customer_type'
  | 'supplier_group'
  | 'service_type'
  | 'receipt_voucher_type_customer'  // Receipt voucher type for customer payments
  | 'receipt_voucher_type_other';    // Receipt voucher type for other purposes

export interface RawMaterial {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  unit: string;
  price: number;
  stock: number;
  minStock: number;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

// Form interfaces for creating/editing
export interface CreateCategoryRequest {
  name: string;
  description?: string; // Changed from notes to match backend
  type: CategoryType; // Added required type field
  parentId?: string; // Added parent ID for hierarchy
  status: CategoryStatus;
  for_semi_material?: boolean; // Added semi-material flag
}

export interface UpdateCategoryRequest extends Partial<CreateCategoryRequest> {
  id: string;
}

// Filter and search interfaces
export interface CategoryFilters {
  status?: CategoryStatus[];
  type?: CategoryType[]; // Added type filter
  search?: string;
  creator?: string;
  createdFrom?: string;
  createdTo?: string;
  hasLinkedMaterials?: boolean;
  parentId?: string; // Added parent filter
}

// API Response interfaces
export interface CategoryListResponse {
  data: Category[];
  total: number;
  page: number;
  pageSize: number;
}

export interface CategoryDetailResponse {
  category: Category;
  rawMaterials: RawMaterial[];
  totalMaterials: number;
}

// Export options
export interface ExportCategoryOptions {
  dateRange: 'all' | 'thisMonth' | 'lastMonth' | 'thisYear' | 'custom';
  customDateFrom?: string;
  customDateTo?: string;
  includeDetails: boolean;
  includeNotes: boolean;
  format: 'excel' | 'pdf' | 'csv';
  types?: CategoryType[]; // Added type filter for export
}

// Category type labels for display
export const CATEGORY_TYPE_LABELS: Record<CategoryType, string> = {
  material_category: 'Danh mục nguyên liệu',
  material_group: 'Nhóm nguyên liệu',
  customer_group: 'Nhóm khách hàng',
  customer_type: 'Loại khách hàng',
  supplier_group: 'Nhóm nhà cung cấp',
  service_type: 'Loại dịch vụ',
  receipt_voucher_type_customer: 'Loại phiếu thu khách hàng',
  receipt_voucher_type_other: 'Loại phiếu thu khác',
};

// Receipt voucher category types for filtering
export const RECEIPT_VOUCHER_CATEGORY_TYPES: CategoryType[] = [
  'receipt_voucher_type_customer',
  'receipt_voucher_type_other'
];

// Category status labels for display
export const CATEGORY_STATUS_LABELS: Record<CategoryStatus, string> = {
  active: 'Đang hoạt động',
  inactive: 'Ngừng hoạt động',
};

// Helper function to get category type label
export const getCategoryTypeLabel = (type: CategoryType): string => {
  return CATEGORY_TYPE_LABELS[type] || type;
};

// Helper function to get category status label
export const getCategoryStatusLabel = (status: CategoryStatus): string => {
  return CATEGORY_STATUS_LABELS[status] || status;
};
